import { useMemo, useState } from 'react'
import { Search, Trash2, Loader2, Inbox } from 'lucide-react'
import { isLive, originHref } from './chatRuns'

/**
 * Case- and accent-insensitive: an editor typing "citation" must find
 * "Citation" and "Génération" must be reachable as "generation".
 */
const fold = (s) => (s ?? '')
  .normalize('NFD')
  .replace(/\p{Diacritic}/gu, '')
  .toLowerCase()

/**
 * Conversation picker for the builder chat.
 *
 * Purely presentational: the panel owns the entries (read from IndexedDB via
 * `loadBuilderHistory`) and both callbacks. Resuming hands the WHOLE entry back
 * — `sessionId` included, which is what lets the agent pick the conversation up
 * instead of starting over.
 *
 * The filter is view state and lives here: the component unmounts when the
 * picker closes, so it resets on its own.
 *
 * Only the component is exported: a non-component export in a `.jsx` breaks
 * Fast Refresh, and the front is served by Vite HMR.
 */
export function BuilderChatHistory({ id, entries, currentId, onResume, onDelete }) {
  const [query, setQuery] = useState('')

  // Matches what the row actually SHOWS — its title and the address it started
  // from. Matching the hidden transcript would surface rows with no visible
  // reason to be there.
  const shown = useMemo(() => {
    const needle = fold(query.trim())
    if (!needle) return entries
    return entries.filter((e) => fold(`${e.title} ${e.origin ?? ''}`).includes(needle))
  }, [entries, query])

  if (entries.length === 0) {
    return (
      <div id={id} className="flex-1 overflow-y-auto p-4">
        <p className="text-sm text-gray-500">
          Aucune conversation mémorisée pour l'instant.
        </p>
      </div>
    )
  }

  return (
    <div id={id} className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // A placeholder is not an accessible name.
          aria-label="Filtrer les conversations"
          placeholder="Filtrer…"
          className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-2 text-sm placeholder:text-gray-400 focus:border-teal-400 focus:outline-none"
        />
      </div>

      {shown.length === 0 ? (
        // role="status" so the change is announced, not just seen.
        <p role="status" className="px-1 py-2 text-sm text-gray-500">
          Aucune conversation ne correspond à « {query.trim()} ».
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {shown.map((entry) => {
            const title = entry.title || 'Conversation'
            const isCurrent = entry.id === currentId
            // Deux gestes distincts sur la même ligne : le TITRE rouvre la
            // conversation ici, l'ADRESSE emmène sur la page d'où elle est
            // partie — avec l'assistant rouvert dessus.
            const href = originHref(entry.origin, entry.id)
            return (
              // La CARTE est le <li>, pas le bouton de reprise : l'adresse est
              // un lien, et un élément interactif dans un autre est invalide —
              // le clic intérieur y serait avalé. Bouton de reprise, lien
              // d'adresse et bouton de suppression sont donc trois frères.
              <li
                key={entry.id}
                className={`card relative py-2.5 pl-3 pr-10 text-sm transition-colors hover:border-teal-300 hover:bg-teal-50/60 ${
                  isCurrent ? 'border-teal-300 bg-teal-50/60' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => onResume(entry)}
                  aria-current={isCurrent ? 'true' : undefined}
                  aria-label={`${title}${entry.origin ? ` — depuis ${entry.origin}` : ''}${isLive(entry.run) ? ' — réponse en cours' : ''}${!isLive(entry.run) && entry.run?.delivered === false ? ' — réponse terminée, non lue' : ''}`}
                  className={`block w-full truncate text-left ${isCurrent ? 'font-medium' : ''}`}
                >
                  {title}
                </button>
                {/* L'écran d'où la conversation est partie. Deux titres se
                    ressemblent vite ; l'adresse est souvent ce qui distingue
                    « ce que j'ai demandé sur CETTE page » du reste — et elle y
                    ramène en un clic. */}
                {entry.origin && (
                  <div className="mt-0.5 truncate text-[11px] text-gray-500">
                    {href ? (
                      // Le nom accessible dit les DEUX effets : l'adresse seule
                      // laisserait croire à un simple changement de page.
                      <a
                        href={href}
                        aria-label={`Aller sur ${entry.origin} avec cette conversation ouverte`}
                        title={`Aller sur ${entry.origin} avec cette conversation ouverte`}
                        className="underline underline-offset-2 hover:text-teal-700"
                      >
                        {entry.origin}
                      </a>
                    ) : entry.origin}
                  </div>
                )}
                {/* Ce que seul le SERVEUR sait : un tour qui tourne encore
                    (peut-être depuis un autre onglet), ou une réponse arrivée
                    pendant que l'éditeur n'était pas là. */}
                {isLive(entry.run) && (
                  <span className="mt-1 flex items-center gap-1 text-[11px] text-teal-700">
                    <Loader2 size={11} className="animate-spin flex-shrink-0" aria-hidden="true" />
                    {entry.run.status === 'queued'
                      ? `En attente d’une place${entry.run.queuePosition ? ` (${entry.run.queuePosition}e)` : ''}`
                      : 'Réponse en cours…'}
                  </span>
                )}
                {!isLive(entry.run) && entry.run?.delivered === false && (
                  <span className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
                    <Inbox size={11} className="flex-shrink-0" aria-hidden="true" />
                    Réponse terminée, non lue
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(entry)}
                  // Named, not a bare "Supprimer" repeated down the list.
                  aria-label={`Supprimer la conversation « ${title} »`}
                  className="absolute top-1/2 right-2 -translate-y-1/2 p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
